import { BatteryRange } from './battery-range.model';
import { BatteryLight } from './battery.model';

import { LoadMode } from '../enums';

export type ResultStatus = 'selected' | 'feasible' | 'infeasible';

export type KtDatasetType = 'current' | 'power';

export interface BatterySizingRequest {
  clientDocumentId?: string;
  backupMinutes: number;
  minCellVoltage: number;
  sizingMode: LoadMode;
  loadValue: number;
  powerFactor?: number;
  upsEfficiency?: number;
  designTemperature: number;
  designCorrectionFactor?: number;
  agingCorrectionFactor?: number;
  blocVoltage: number;
  batteryRanges?: BatteryRange[];
  nbBlocks?: number;
  nbBlocksMax?: number;
}

export interface BatterySizingResponse {
  input: BatterySizingRequest;
  ktType: KtDatasetType;
  seriesBlocks: number;
  ranges: BatterySizingRangeResult[];
}

export interface BatterySizingRangeResult {
  rangeCode: string;
  rangeName?: string | null;
  range?: BatteryRange;
  status: ResultStatus;
  reason?: string;
  batteryUnitPrice?: number | null;
  batteryTotalPrice?: number | null;
  configuration?: BatterySizingConfiguration;
}

export interface BatteryVentilation {
  float: {
    airExchangeM3PerH: number;
    openingAreaCm2: number;
    safetyDistanceM: number;
  };
  boost: {
    airExchangeM3PerH: number;
    openingAreaCm2: number;
    safetyDistanceM: number;
  };
}

export interface CableRecommendation {
  ampacityMm2: number;
  governing: string;
  recommendedMm2: number;
  totalCurrentA?: number;
  perStringCurrentA?: number;
  shortCircuitMm2?: number;
  shortCircuitRawMm2?: number;
}

export interface BatterySizingConfiguration {
  batteryDocumentId: string;
  batteryPartNumber: string;
  parallels: number;
  seriesBlocks: number;
  maxSectionSize: number;
  requiredKt: number;
  offeredKt: number;
  c10RequiredAh: number;
  c10NominalAh: number;
  c10OutputLabelAh?: number | null;
  ktType?: KtDatasetType;
  designFactor?: number;
  ageingFactor?: number;
  temperatureFactor?: number;
  totalCorrectionFactor?: number;
  realBackupMinutes?: number;
  diagnostics?: BatterySizingDiagnostics;
  ventilation?: BatteryVentilation;
  cableRecommendation?: CableRecommendation;
}

export interface BatterySizingDiagnostics {
  interpolation: InterpolationTrace;
  temperature: TemperatureInterpolationTrace;
  perStringLoad: number;
  perStringLoadUnit: 'amps' | 'watts';
  maxSectionSize?: number;
  requiredKt?: number;
  offeredKt?: number;
  searchIterations: number;
}

export interface InterpolationTrace {
  timeLower: number;
  timeUpper: number;
  voltageLower: number;
  voltageUpper: number;
  nodes: InterpolationNode[];
  method: 'bilinear' | 'linear';
}

export interface TemperatureInterpolationTrace {
  minutesLower: number;
  minutesUpper: number;
  temperatureLower: number;
  temperatureUpper: number;
  factors: number[];
  method: 'bilinear' | 'linear';
}

export interface InterpolationNode {
  timeMinutes: number;
  voltageVpc: number;
  kt: number;
  inverseKt: number;
}

export interface ProjectSizingToolConfiguration {
  id?: number;
  battery?: BatteryLight;
  batteryId?: string;
  batteryDocumentId?: string;
  parallels?: number;
  seriesBlocks?: number;
  c10RequiredAh?: number;
  c10NominalAh?: number;
  c10OutputLabelAh?: number | null;
  designFactor?: number;
  ageingFactor?: number;
  temperatureFactor?: number;
  totalCorrectionFactor?: number;
  maxSectionSize?: number;
  requiredKt?: number;
  offeredKt?: number;
  realBackupMinutes?: number;
  ventilation?: BatteryVentilation;
  cableRecommendation?: CableRecommendation;
}

export interface ProjectSizingToolConfigurationCreate {
  id?: number;
  battery?: string | null;
  parallels?: number;
  seriesBlocks?: number;
  c10RequiredAh?: number;
  c10NominalAh?: number;
  c10OutputLabelAh?: number | null;
  designFactor?: number;
  ageingFactor?: number;
  temperatureFactor?: number;
  totalCorrectionFactor?: number;
  maxSectionSize?: number;
  requiredKt?: number;
  offeredKt?: number;
  realBackupMinutes?: number;
  ventilation?: BatteryVentilation;
  cableRecommendation?: CableRecommendation;
}

export interface ProjectSizingToolResult {
  id?: number;
  battery_range?: BatteryRange;
  resultStatus?: ResultStatus;
  configuration?: ProjectSizingToolConfiguration;
  batteryUnitPrice?: number | null;
  batteryTotalPrice?: number | null;
}

export interface ProjectSizingToolResultCreate {
  id?: number;
  battery_range?: string | null;
  resultStatus?: ResultStatus;
  configuration?: ProjectSizingToolConfigurationCreate;
  batteryUnitPrice?: number | null;
  batteryTotalPrice?: number | null;
}

// Map LoadMode to a user-friendly label
export function loadModeToLabel(
  mode?: LoadMode | string | null,
): 'Current' | 'Power' | undefined {
  switch (mode) {
    case 'amps':
      return 'Current';
    case 'watts':
    case 'kva':
      return 'Power';
    default:
      return undefined;
  }
}
